import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import LightBackground from '@/components/LightBackground';
import { 
  ArrowLeft, 
  Search, 
  Loader2,
  Users,
  Download,
  RefreshCw,
  Calendar,
  Mail,
  Globe,
  CreditCard,
  Eye,
  Copy,
  ChevronDown,
  ChevronUp,
  Filter,
  Key,
  Clock,
  BookOpen,
  CheckCircle,
  Send,
  KeyRound,
  Star,
  MessageCircle,
  Sparkles,
  Trash2,
  Rocket,
  Heart,
  Gift,
  Zap
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { formatLastActive, getDaysUntilExpiration } from '@/services/clientService';
import { ClientInfoModal } from '@/components/ClientInfoModal';

// Test account passwords are no longer hardcoded client-side.
// Admins should use the standard password reset flow if needed.

interface CRMClient {
  id: string;
  user_id: string | null;
  name: string;
  email: string;
  country: string | null;
  detected_country: string | null;
  plan: string;
  plan_price: number;
  amount_paid: number | null;
  status: string;
  paid: boolean;
  has_access: boolean | null;
  subscription_start: string;
  subscription_end: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  subscription_status: string | null;
  motivation_letter: string | null;
  applied_at: string | null;
  last_active_at: string | null;
  created_at: string;
  updated_at: string;
  billing_name: string | null;
  billing_address: string | null;
}

interface AcademyProgress {
  academy_id: string;
  academy_name: string;
  completed_modules: number;
  total_modules: number;
}

interface ClassCompletion {
  class_id: string;
  score: number;
  total_questions: number;
  passed: boolean;
  completed_at: string;
}

type SortField = 'name' | 'email' | 'plan' | 'status' | 'created_at' | 'subscription_end';
type SortDirection = 'asc' | 'desc';

const AdminCRM = () => {
  const navigate = useNavigate();
  const { user, role, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState<CRMClient[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [expandedData, setExpandedData] = useState<{
    academyProgress: AcademyProgress[];
    classCompletions: ClassCompletion[];
    loading: boolean;
  } | null>(null);
  const [showPassword, setShowPassword] = useState<string | null>(null);
  const [sendingEmail, setSendingEmail] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; client: CRMClient | null }>({ open: false, client: null });
  const [deleting, setDeleting] = useState(false);
  const [selectedClientForModal, setSelectedClientForModal] = useState<CRMClient | null>(null);
  const [syncingStripe, setSyncingStripe] = useState(false);

  type EmailType = "welcome" | "reset_password" | "confirm_plan" | "rate_experience" | "check_in" | "freemium_upgrade" | "freemium_engagement" | "referral_promo" | "low_cost_offer";

  const sendCRMEmail = async (client: CRMClient, emailType: EmailType) => {
    const emailKey = `${client.id}-${emailType}`;
    setSendingEmail(emailKey);
    
    try {
      const { data, error } = await supabase.functions.invoke('send-crm-email', {
        body: {
          email: client.email,
          name: client.name,
          plan: client.plan,
          emailType,
        },
      });

      if (error) throw error;

      const emailLabels: Record<EmailType, string> = {
        welcome: 'Welcome',
        reset_password: 'Password Reset',
        confirm_plan: 'Plan Confirmation',
        rate_experience: 'Rate Experience',
        check_in: 'Check-in',
        freemium_upgrade: 'Upgrade Prompt',
        freemium_engagement: 'Engagement',
        referral_promo: 'Referral Promo',
        low_cost_offer: 'Low Cost Offer',
      };

      toast.success(`${emailLabels[emailType]} email sent to ${client.name}`);
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email');
    }
    
    setSendingEmail(null);
  };

  // Removed custom cursor for light theme

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (role !== 'admin') {
        toast.error('Access denied. Admin privileges required.');
        navigate('/dashboard');
      }
    }
  }, [user, role, authLoading, navigate]);

  const loadClients = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error('Failed to load clients');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (user && role === 'admin') {
      loadClients();
    }
  }, [user, role]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleDeleteClient = async () => {
    if (!deleteConfirm.client) return;
    
    setDeleting(true);
    try {
      // Delete related data first (due to foreign key constraints)
      await supabase
        .from('class_completions')
        .delete()
        .eq('client_id', deleteConfirm.client.id);
      
      await supabase
        .from('client_academy_progress')
        .delete()
        .eq('client_id', deleteConfirm.client.id);
      
      await supabase
        .from('client_module_completions')
        .delete()
        .eq('client_id', deleteConfirm.client.id);
      

      // Delete the client
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', deleteConfirm.client.id);

      if (error) throw error;

      toast.success(`Client "${deleteConfirm.client.name}" deleted successfully`);
      setClients(prev => prev.filter(c => c.id !== deleteConfirm.client!.id));
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error('Failed to delete client');
    }
    setDeleting(false);
    setDeleteConfirm({ open: false, client: null });
  };

  const loadExpandedData = async (clientId: string) => {
    setExpandedData({ academyProgress: [], classCompletions: [], loading: true });
    
    try {
      const [progressRes, completionsRes] = await Promise.all([
        supabase
          .from('client_academy_progress')
          .select('academy_id, academy_name, completed_modules, total_modules')
          .eq('client_id', clientId),
        supabase
          .from('class_completions')
          .select('class_id, score, total_questions, passed, completed_at')
          .eq('client_id', clientId)
          .order('completed_at', { ascending: false })
          .limit(20)
      ]);

      setExpandedData({
        academyProgress: progressRes.data || [],
        classCompletions: completionsRes.data || [],
        loading: false
      });
    } catch (error) {
      console.error('Error loading expanded data:', error);
      setExpandedData({ academyProgress: [], classCompletions: [], loading: false });
    }
  };

  const handleExpandRow = (clientId: string) => {
    if (expandedRow === clientId) {
      setExpandedRow(null);
      setExpandedData(null);
    } else {
      setExpandedRow(clientId);
      loadExpandedData(clientId);
    }
  };


  const getClientTypeLabel = (client: CRMClient) => {
    const plan = client.plan?.toLowerCase() || '';
    const subStatus = client.subscription_status?.toLowerCase() || '';
    
    // Admin test accounts
    if (plan === 'test_lifetime' || plan === 'test-lifetime') return 'Test Client Admin';
    if (plan === 'test_monthly' || plan === 'test-monthly') return 'Test Monthly Admin';
    if (plan === 'admin') return 'Admin';
    
    // Monthly with trial/grace states
    if (plan === 'monthly') {
      if (subStatus === 'trialing') return '🕐 Monthly (Trial)';
      if (subStatus === 'past_due' || subStatus === 'grace_period') return '⚠️ Monthly (Grace)';
      if (client.paid) return 'Monthly';
      return 'Monthly (Unpaid)';
    }
    
    if (plan === 'freemium' || !client.paid) return 'Freemium';
    if (plan === 'lifetime') return 'Lifetime';
    
    return plan || 'Freemium';
  };

  const getClientTypeColor = (client: CRMClient) => {
    const plan = client.plan?.toLowerCase() || '';
    const subStatus = client.subscription_status?.toLowerCase() || '';
    
    if (plan.includes('test')) return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    
    // Monthly lifecycle colors
    if (plan === 'monthly') {
      if (subStatus === 'trialing') return 'bg-cyan-500/20 text-cyan-500 border-cyan-500/30';
      if (subStatus === 'past_due' || subStatus === 'grace_period') return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      if (client.paid) return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    }
    
    if (plan === 'freemium' || !client.paid) return client.user_id 
      ? 'bg-green-500/20 text-green-400 border-green-500/30' 
      : 'bg-red-500/20 text-red-400 border-red-500/30';
    if (plan === 'lifetime') return 'bg-gold/20 text-gold border-gold/30';
    return 'bg-muted/20 text-muted-foreground border-muted/30';
  };

  const getStatusLabel = (client: CRMClient) => {
    const subStatus = client.subscription_status?.toLowerCase() || '';
    const plan = client.plan?.toLowerCase() || '';
    
    // Subscription lifecycle statuses
    if (subStatus === 'trialing') {
      const days = getDaysUntilExpiration(client.subscription_end);
      return `Trial (${days}d left)`;
    }
    if (subStatus === 'grace_period' || (subStatus === 'past_due' && client.has_access)) {
      const days = getDaysUntilExpiration(client.subscription_end);
      return days >= 0 ? `Grace (${days}d left)` : 'Grace Expired';
    }
    if (subStatus === 'past_due' && !client.has_access) return 'Payment Failed';
    
    if (!client.paid && plan !== 'freemium' && plan === 'monthly') return 'Awaiting Payment';
    if (!client.paid) return 'Freemium';
    
    const days = getDaysUntilExpiration(client.subscription_end);
    if (days < 0) return 'Expired';
    if (days <= 30) return 'Expiring Soon';
    return 'Active';
  };

  const getStatusColor = (client: CRMClient) => {
    const subStatus = client.subscription_status?.toLowerCase() || '';
    
    if (subStatus === 'trialing') return 'bg-cyan-500/20 text-cyan-500';
    if (subStatus === 'grace_period' || (subStatus === 'past_due' && client.has_access)) {
      const days = getDaysUntilExpiration(client.subscription_end);
      return days >= 0 ? 'bg-orange-500/20 text-orange-400' : 'bg-red-500/20 text-red-400';
    }
    if (subStatus === 'past_due') return 'bg-red-500/20 text-red-400';
    
    if (!client.paid) return 'bg-yellow-500/20 text-yellow-400';
    const days = getDaysUntilExpiration(client.subscription_end);
    if (days < 0) return 'bg-red-500/20 text-red-400';
    if (days <= 30) return 'bg-orange-500/20 text-orange-400';
    return 'bg-green-500/20 text-green-400';
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedClients = clients
    .filter(client => {
      const matchesSearch = 
        client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (client.country?.toLowerCase().includes(searchQuery.toLowerCase()) || false);
      
      const plan = client.plan?.toLowerCase() || '';
      let matchesPlan = true;
      const subStatus = client.subscription_status?.toLowerCase() || '';
      if (planFilter === 'all') {
        matchesPlan = true;
      } else if (planFilter === 'freemium') {
        matchesPlan = plan === 'freemium' || (!client.paid && plan !== 'monthly');
      } else if (planFilter === 'lifetime') {
        matchesPlan = client.paid && plan === 'lifetime';
      } else if (planFilter === 'monthly') {
        matchesPlan = plan === 'monthly' && subStatus !== 'trialing' && subStatus !== 'past_due' && subStatus !== 'grace_period';
      } else if (planFilter === 'trial') {
        matchesPlan = subStatus === 'trialing';
      } else if (planFilter === 'grace') {
        matchesPlan = subStatus === 'past_due' || subStatus === 'grace_period';
      } else if (planFilter === 'admin') {
        matchesPlan = plan === 'admin';
      } else if (planFilter === 'test_monthly') {
        matchesPlan = plan === 'test_monthly' || plan === 'test-monthly';
      } else if (planFilter === 'test_lifetime') {
        matchesPlan = plan === 'test_lifetime' || plan === 'test-lifetime';
      }
      
      let matchesStatus = true;
      if (statusFilter === 'pending') matchesStatus = !client.paid;
      else if (statusFilter === 'active') matchesStatus = client.paid && getDaysUntilExpiration(client.subscription_end) >= 0;
      else if (statusFilter === 'expired') matchesStatus = client.paid && getDaysUntilExpiration(client.subscription_end) < 0;
      
      return matchesSearch && matchesPlan && matchesStatus;
    })
    .sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'email':
          comparison = a.email.localeCompare(b.email);
          break;
        case 'plan':
          comparison = a.plan.localeCompare(b.plan);
          break;
        case 'status':
          comparison = (a.paid ? 1 : 0) - (b.paid ? 1 : 0);
          break;
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'subscription_end':
          comparison = new Date(a.subscription_end).getTime() - new Date(b.subscription_end).getTime();
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Country', 'Plan', 'Price', 'Paid', 'Status', 'Created', 'Subscription Start', 'Subscription End', 'Last Active'];
    const rows = filteredAndSortedClients.map(c => [
      c.name,
      c.email,
      c.country || '',
      c.plan,
      c.plan_price,
      c.paid ? 'Yes' : 'No',
      getStatusLabel(c),
      new Date(c.created_at).toLocaleDateString(),
      c.subscription_start,
      c.subscription_end,
      c.last_active_at ? new Date(c.last_active_at).toLocaleDateString() : 'Never'
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clients-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported to CSV');
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const planCounts = {
    all: clients.length,
    freemium: clients.filter(c => (!c.paid && c.plan.toLowerCase() === 'freemium') || (!c.paid && !c.plan)).length,
    lifetime: clients.filter(c => c.paid && c.plan.toLowerCase() === 'lifetime').length,
    monthly: clients.filter(c => c.plan.toLowerCase() === 'monthly' && c.subscription_status !== 'trialing' && c.subscription_status !== 'past_due' && c.subscription_status !== 'grace_period').length,
    trial: clients.filter(c => c.subscription_status === 'trialing').length,
    grace: clients.filter(c => c.subscription_status === 'past_due' || c.subscription_status === 'grace_period').length,
    admin: clients.filter(c => c.plan.toLowerCase() === 'admin').length,
    testMonthly: clients.filter(c => c.plan.toLowerCase() === 'test_monthly' || c.plan.toLowerCase() === 'test-monthly').length,
    testClient: clients.filter(c => c.plan.toLowerCase() === 'test_lifetime' || c.plan.toLowerCase() === 'test-lifetime').length,
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      <LightBackground variant="subtle" />

      {/* Header */}
      <motion.header
        className="relative z-20 p-6 flex justify-between items-center border-b border-border bg-white/80 backdrop-blur-sm"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            className="text-muted-foreground hover:text-foreground"
            onClick={() => navigate('/admin')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <span className="text-lg font-semibold text-foreground">
            Faceless Aura <span className="text-primary">Club</span>
          </span>
          <div className="h-6 w-px bg-border" />
          <span className="text-sm font-medium text-primary">CRM Database</span>
        </div>
        <div className="flex items-center gap-2 md:gap-4 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/launch-kit-briefs')}
            className="flex items-center gap-2 border-blue-200 text-blue-700 hover:bg-blue-50"
          >
            <Rocket className="w-4 h-4" />
            <span className="hidden sm:inline">Launch Kit Briefs</span>
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={async () => {
              setSyncingStripe(true);
              try {
                const { data: sessionData } = await supabase.auth.getSession();
                if (!sessionData?.session?.access_token) {
                  toast.error('Not authenticated');
                  return;
                }
                const { data, error } = await supabase.functions.invoke('sync-all-stripe-status', {
                  headers: {
                    Authorization: `Bearer ${sessionData.session.access_token}`,
                  },
                });
                if (error) throw error;
                toast.success(`Stripe sync complete: ${data.updated} updated, ${data.noStripeCustomer} no Stripe customer`);
                loadClients();
              } catch (err: any) {
                toast.error(err.message || 'Stripe sync failed');
              } finally {
                setSyncingStripe(false);
              }
            }}
            disabled={syncingStripe}
            className="flex items-center gap-2"
          >
            {syncingStripe ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CreditCard className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">Sync Stripe</span>
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            className="text-muted-foreground hover:text-foreground"
            onClick={loadClients}
          >
            <RefreshCw className="w-5 h-5" />
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={exportToCSV}
            className="flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export CSV</span>
          </Button>
        </div>
      </motion.header>

      {/* Main content */}
      <div className="relative z-10 max-w-full mx-auto px-6 py-8">
        <motion.div
          className="mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <h1 className="text-2xl md:text-3xl font-light tracking-wide mb-2">
            <span className="text-foreground">Client Database</span>
          </h1>
          <p className="text-muted-foreground text-sm">
            Complete CRM view of all {clients.length} clients
          </p>
        </motion.div>

        {/* Stats Row */}
        <motion.div
          className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-3 mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
        >
          {[
            { label: 'Total', count: planCounts.all, color: 'text-foreground' },
            { label: 'Freemium', count: planCounts.freemium, color: 'text-gray-400' },
            { label: 'Lifetime', count: planCounts.lifetime, color: 'text-gold' },
            { label: 'Monthly', count: planCounts.monthly, color: 'text-blue-400' },
            { label: '🕐 Trial', count: planCounts.trial, color: 'text-cyan-500' },
            { label: '⚠️ Grace', count: planCounts.grace, color: 'text-orange-400' },
            { label: 'Admin', count: planCounts.admin, color: 'text-red-400' },
            { label: 'Test Monthly', count: planCounts.testMonthly, color: 'text-purple-400' },
            { label: 'Test Client', count: planCounts.testClient, color: 'text-pink-400' },
          ].map((stat, i) => (
            <div key={i} className="p-4 border border-border/30 bg-card/30 backdrop-blur-sm rounded-lg text-center">
              <p className={`text-2xl font-light ${stat.color}`}>{stat.count}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">{stat.label}</p>
            </div>
          ))}
        </motion.div>

        {/* Filters */}
        <motion.div
          className="flex flex-wrap gap-4 mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, country..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-background/50 border-border/30"
            />
          </div>
          
          <div className="flex items-center gap-2 bg-muted/20 rounded-lg p-1 flex-wrap">
            <Filter className="w-4 h-4 text-muted-foreground ml-2" />
            {[
              { key: 'all', label: 'All' },
              { key: 'freemium', label: 'Freemium' },
              { key: 'lifetime', label: 'Lifetime' },
              { key: 'monthly', label: 'Monthly' },
              { key: 'trial', label: '🕐 Trial' },
              { key: 'grace', label: '⚠️ Grace' },
              { key: 'admin', label: 'Admin' },
              { key: 'test_monthly', label: 'Test Monthly' },
              { key: 'test_lifetime', label: 'Test Client' },
            ].map((filter) => (
              <button
                key={filter.key}
                onClick={() => setPlanFilter(filter.key)}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  planFilter === filter.key 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1 bg-muted/20 rounded-lg p-1">
            {['all', 'pending', 'active', 'expired'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  statusFilter === status 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="border border-border/30 bg-card/30 backdrop-blur-sm rounded-lg overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border/20 bg-muted/10">
                  <th 
                    className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Name <SortIcon field="name" />
                    </div>
                  </th>
                  <th 
                    className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                    onClick={() => handleSort('email')}
                  >
                    <div className="flex items-center gap-1">
                      Email <SortIcon field="email" />
                    </div>
                  </th>
                  <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Country
                  </th>
                  <th 
                    className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                    onClick={() => handleSort('plan')}
                  >
                    <div className="flex items-center gap-1">
                      Type <SortIcon field="plan" />
                    </div>
                  </th>
                  <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Price
                  </th>
                  <th 
                    className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center gap-1">
                      Status <SortIcon field="status" />
                    </div>
                  </th>
                  <th 
                    className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center gap-1">
                      Created <SortIcon field="created_at" />
                    </div>
                  </th>
                  <th 
                    className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                    onClick={() => handleSort('subscription_end')}
                  >
                    <div className="flex items-center gap-1">
                      Expires <SortIcon field="subscription_end" />
                    </div>
                  </th>
                  <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Last Active
                  </th>
                  <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedClients.map((client, index) => (
                  <>
                    <motion.tr
                      key={client.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.2, delay: index * 0.02 }}
                      className="border-b border-border/10 hover:bg-muted/5 transition-colors"
                    >
                      <td className="p-4">
                        <div>
                          <p className="text-sm font-medium text-foreground">{client.name}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-foreground font-mono">{client.email}</span>
                          <button 
                            onClick={() => copyToClipboard(client.email)}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Globe className="w-3 h-3 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">{client.country || '-'}</span>
                          {client.detected_country && client.country && client.detected_country !== client.country && (
                            <span title={`IP detected: ${client.detected_country}`} className="text-amber-500 text-xs font-medium flex items-center gap-0.5">
                              ⚠️
                            </span>
                          )}
                          {!client.detected_country && client.country && (
                            <span title="No IP verification available" className="text-muted-foreground/50 text-xs">?</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`text-xs px-2 py-1 rounded-full border ${getClientTypeColor(client)}`}>
                          {getClientTypeLabel(client)}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="text-sm text-foreground">
                          {client.plan === 'Monthly' ? '$19/mo' : client.plan === 'Lifetime' ? '$97' : client.plan === 'Freemium' ? 'Free' : `$${client.plan_price}`}
                          {client.amount_paid != null && client.amount_paid > 0 && client.amount_paid !== client.plan_price && (
                            <span className="block text-xs text-muted-foreground">paid: ${Number(client.amount_paid).toFixed(2)}</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(client)}`}>
                          {getStatusLabel(client)}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          {new Date(client.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-xs text-muted-foreground">
                          {client.subscription_end}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="text-xs text-muted-foreground">
                          {formatLastActive(client.last_active_at)}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedClientForModal(client)}
                            className="text-muted-foreground hover:text-foreground"
                            title="View Client File"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground hover:text-foreground"
                                disabled={sendingEmail?.startsWith(client.id)}
                              >
                                {sendingEmail?.startsWith(client.id) ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Mail className="w-4 h-4" />
                                )}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuLabel className="text-xs text-muted-foreground">
                                Quick Send Email
                              </DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => sendCRMEmail(client, 'welcome')}
                                className="cursor-pointer"
                              >
                                <Sparkles className="w-4 h-4 mr-2 text-primary" />
                                Welcome
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => sendCRMEmail(client, 'reset_password')}
                                className="cursor-pointer"
                              >
                                <KeyRound className="w-4 h-4 mr-2 text-orange-400" />
                                Reset Password
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => sendCRMEmail(client, 'confirm_plan')}
                                className="cursor-pointer"
                              >
                                <CreditCard className="w-4 h-4 mr-2 text-blue-400" />
                                Confirm Plan
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => sendCRMEmail(client, 'rate_experience')}
                                className="cursor-pointer"
                              >
                                <Star className="w-4 h-4 mr-2 text-yellow-400" />
                                Rate Experience
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => sendCRMEmail(client, 'check_in')}
                                className="cursor-pointer"
                              >
                                <MessageCircle className="w-4 h-4 mr-2 text-green-400" />
                                Check-in
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuLabel className="text-xs text-muted-foreground">
                                Freemium Drip
                              </DropdownMenuLabel>
                              <DropdownMenuItem
                                onClick={() => sendCRMEmail(client, 'freemium_upgrade')}
                                className="cursor-pointer"
                              >
                                <Rocket className="w-4 h-4 mr-2 text-purple-400" />
                                Upgrade Prompt
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => sendCRMEmail(client, 'freemium_engagement')}
                                className="cursor-pointer"
                              >
                                <Heart className="w-4 h-4 mr-2 text-pink-400" />
                                Engagement / Come Back
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuLabel className="text-xs text-muted-foreground">
                                Promotions
                              </DropdownMenuLabel>
                              <DropdownMenuItem
                                onClick={() => sendCRMEmail(client, 'referral_promo')}
                                className="cursor-pointer"
                              >
                                <Gift className="w-4 h-4 mr-2 text-emerald-400" />
                                Referral Program
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => sendCRMEmail(client, 'low_cost_offer')}
                                className="cursor-pointer"
                              >
                                <Zap className="w-4 h-4 mr-2 text-amber-400" />
                                $4.99/mo Low Cost Offer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirm({ open: true, client })}
                            className="text-muted-foreground hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </motion.tr>
                    {expandedRow === client.id && (
                      <motion.tr
                        key={`${client.id}-expanded`}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="bg-muted/5"
                      >
                        <td colSpan={10} className="p-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {/* Account & Authentication */}
                            <div className="space-y-3">
                              <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                                <Key className="w-4 h-4 text-primary" />
                                Account & Authentication
                              </h4>
                              <div className="space-y-2 text-xs">
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Client ID:</span>
                                  <div className="flex items-center gap-1">
                                    <span className="font-mono text-foreground">{client.id.slice(0, 8)}...</span>
                                    <button onClick={() => copyToClipboard(client.id)} className="text-muted-foreground hover:text-foreground">
                                      <Copy className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">User ID:</span>
                                  <div className="flex items-center gap-1">
                                    <span className="font-mono text-foreground">{client.user_id ? client.user_id.slice(0, 8) + '...' : 'None'}</span>
                                    {client.user_id && (
                                      <button onClick={() => copyToClipboard(client.user_id!)} className="text-muted-foreground hover:text-foreground">
                                        <Copy className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Email:</span>
                                  <span className="font-mono text-foreground">{client.email}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Has Access:</span>
                                  <span className={client.has_access ? 'text-green-400' : 'text-red-400'}>
                                    {client.has_access ? 'Yes' : 'No'}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Paid:</span>
                                  <span className={client.paid ? 'text-green-400' : 'text-red-400'}>
                                    {client.paid ? 'Yes' : 'No'}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Plan:</span>
                                  <span className="text-foreground">{client.plan}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Status:</span>
                                  <span className="text-foreground">{client.status}</span>
                                </div>
                              </div>
                            </div>

                            {/* Dates & Timestamps */}
                            <div className="space-y-3">
                              <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                                <Clock className="w-4 h-4 text-primary" />
                                Dates & Activity
                              </h4>
                              <div className="space-y-2 text-xs">
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Created:</span>
                                  <span className="text-foreground">{new Date(client.created_at).toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Updated:</span>
                                  <span className="text-foreground">{new Date(client.updated_at).toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Applied:</span>
                                  <span className="text-foreground">
                                    {client.applied_at ? new Date(client.applied_at).toLocaleString() : 'N/A'}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Sub Start:</span>
                                  <span className="text-foreground">{client.subscription_start}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Sub End:</span>
                                  <span className="text-foreground">{client.subscription_end}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Last Active:</span>
                                  <span className="text-foreground">
                                    {client.last_active_at ? new Date(client.last_active_at).toLocaleString() : 'Never'}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Last Login:</span>
                                  <span className="text-foreground">{formatLastActive(client.last_active_at)}</span>
                                </div>
                              </div>
                            </div>

                            {/* Payment & Stripe */}
                            <div className="space-y-3">
                              <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                                <CreditCard className="w-4 h-4 text-primary" />
                                Payment & Stripe
                              </h4>
                              <div className="space-y-2 text-xs">
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Plan Price:</span>
                                  <span className="text-foreground">${client.plan_price}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Customer ID:</span>
                                  <div className="flex items-center gap-1">
                                    <span className="font-mono text-foreground text-[10px]">{client.stripe_customer_id || 'None'}</span>
                                    {client.stripe_customer_id && (
                                      <button onClick={() => copyToClipboard(client.stripe_customer_id!)} className="text-muted-foreground hover:text-foreground">
                                        <Copy className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Subscription ID:</span>
                                  <div className="flex items-center gap-1">
                                    <span className="font-mono text-foreground text-[10px]">{client.stripe_subscription_id || 'None'}</span>
                                    {client.stripe_subscription_id && (
                                      <button onClick={() => copyToClipboard(client.stripe_subscription_id!)} className="text-muted-foreground hover:text-foreground">
                                        <Copy className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Sub Status:</span>
                                  <span className="text-foreground">{client.subscription_status || 'N/A'}</span>
                                </div>
                              </div>
                            </div>

                            {/* Application Info */}
                            <div className="space-y-3">
                              <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                                <Mail className="w-4 h-4 text-primary" />
                                Application
                              </h4>
                              <div className="space-y-2 text-xs">
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Country:</span>
                                  <span className="text-foreground">{client.country || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">IP Country:</span>
                                  <span className={`text-foreground ${client.detected_country && client.country && client.detected_country !== client.country ? 'text-amber-500 font-medium' : ''}`}>
                                    {client.detected_country || 'Not verified'}
                                    {client.detected_country && client.country && client.detected_country !== client.country && ' ⚠️'}
                                  </span>
                                </div>
                                {client.motivation_letter && (
                                  <div className="mt-2">
                                    <span className="text-muted-foreground block mb-1">Motivation Letter:</span>
                                    <p className="text-foreground bg-muted/20 p-2 rounded text-xs max-h-20 overflow-y-auto">
                                      {client.motivation_letter}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Academy Progress & Usage Stats */}
                          <div className="mt-6 pt-6 border-t border-border/20">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              {/* Academy Progress */}
                              <div className="space-y-3">
                                <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                                  <BookOpen className="w-4 h-4 text-primary" />
                                  Academy Progress
                                </h4>
                                {expandedData?.loading ? (
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Loading progress...
                                  </div>
                                ) : expandedData?.academyProgress && expandedData.academyProgress.length > 0 ? (
                                  <div className="space-y-2">
                                    {expandedData.academyProgress.map((progress) => (
                                      <div key={progress.academy_id} className="flex items-center justify-between text-xs">
                                        <span className="text-muted-foreground">{progress.academy_name}:</span>
                                        <div className="flex items-center gap-2">
                                          <div className="w-24 h-2 bg-muted/20 rounded-full overflow-hidden">
                                            <div 
                                              className="h-full bg-primary rounded-full transition-all"
                                              style={{ width: `${(progress.completed_modules / progress.total_modules) * 100}%` }}
                                            />
                                          </div>
                                          <span className="text-foreground font-mono">
                                            {progress.completed_modules}/{progress.total_modules}
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-xs text-muted-foreground">No academy progress recorded</p>
                                )}
                              </div>

                              {/* Recent Class Completions */}
                              <div className="space-y-3">
                                <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                                  <CheckCircle className="w-4 h-4 text-primary" />
                                  Recent Class Completions
                                </h4>
                                {expandedData?.loading ? (
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Loading completions...
                                  </div>
                                ) : expandedData?.classCompletions && expandedData.classCompletions.length > 0 ? (
                                  <div className="space-y-1 max-h-32 overflow-y-auto">
                                    {expandedData.classCompletions.map((completion, i) => (
                                      <div key={i} className="flex items-center justify-between text-xs bg-muted/10 p-2 rounded">
                                        <span className="text-muted-foreground font-mono">{completion.class_id.slice(0, 8)}...</span>
                                        <div className="flex items-center gap-2">
                                          <span className={completion.passed ? 'text-green-400' : 'text-red-400'}>
                                            {completion.score}/{completion.total_questions}
                                          </span>
                                          <span className="text-muted-foreground">
                                            {new Date(completion.completed_at).toLocaleDateString()}
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-xs text-muted-foreground">No class completions yet</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </motion.tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>

          {filteredAndSortedClients.length === 0 && (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">No clients found matching your filters.</p>
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-4 text-center text-xs text-muted-foreground"
        >
          Showing {filteredAndSortedClients.length} of {clients.length} clients
        </motion.div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, client: null })}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Delete Client</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Are you sure you want to permanently delete <strong className="text-foreground">{deleteConfirm.client?.name}</strong> ({deleteConfirm.client?.email})?
              <br /><br />
              This will also delete all their:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Academy progress</li>
                <li>Class completions</li>
                <li>Module completions</li>
                <li>Release requests</li>
              </ul>
              <br />
              <span className="text-red-400 font-medium">This action cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} className="border-border">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeleteClient();
              }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Client
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Client Info Popup Modal */}
      {selectedClientForModal && (
        <ClientInfoModal
          client={selectedClientForModal}
          onClose={() => setSelectedClientForModal(null)}
          onClientUpdated={loadClients}
        />
      )}
    </div>
  );
};

export default AdminCRM;
